import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, UploadIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
const FileComplaint = () => {
  const {
    orderId
  } = useParams();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    description: '',
    imageUrl: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Load order details
  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate('/login');
      return;
    }
    // Get order from localStorage
    const savedOrders = JSON.parse(localStorage.getItem('orders') || '[]');
    const currentOrder = savedOrders.find(o => o.id === orderId);
    if (currentOrder) {
      setOrder(currentOrder);
    } else {
      navigate('/orders');
    }
    setIsLoading(false);
  }, [orderId, user, navigate]);
  // Handle form input change
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  // Handle image upload
  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (file) {
      // In a real app, you would upload the file to a server
      // Here we'll just create a fake URL
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        imageUrl
      });
    }
  };
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.description.trim()) {
      newErrors.description = 'Please describe your issue';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Submit complaint
  const handleSubmit = e => {
    e.preventDefault();
    setIsSubmitting(true);
    if (validateForm()) {
      // Create complaint object
      const complaint = {
        id: Date.now(),
        orderId: order.id,
        customerId: user.id,
        customer: user.name,
        date: new Date().toISOString(),
        description: formData.description,
        imageUrl: formData.imageUrl,
        status: 'Open',
        dateSubmitted: new Date().toISOString()
      };
      // Save complaint to localStorage
      const complaints = JSON.parse(localStorage.getItem('complaints') || '[]');
      complaints.push(complaint);
      localStorage.setItem('complaints', JSON.stringify(complaints));
      // Show success message
      alert('Your complaint has been submitted. We will review it shortly.');
      // Redirect to orders page
      navigate('/orders');
    }
    setIsSubmitting(false);
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-1194">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800" data-id="element-1195"></div>
      </div>;
  }
  return <div className="bg-amber-50 min-h-screen py-8" data-id="element-1196">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1197">
        <div className="flex items-center mb-6" data-id="element-1198">
          <button onClick={() => navigate(-1)} className="mr-4 text-amber-800 hover:text-amber-900" data-id="element-1199">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-1200" />
          </button>
          <h1 className="text-2xl font-semibold text-amber-900" data-id="element-1201">
            File a Complaint
          </h1>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1202">
          <div className="mb-6" data-id="element-1203">
            <h2 className="text-lg font-medium text-amber-800 mb-2" data-id="element-1204">
              Order Information
            </h2>
            <div className="bg-amber-50 rounded-md p-4" data-id="element-1205">
              <div className="grid grid-cols-2 gap-4" data-id="element-1206">
                <div data-id="element-1207">
                  <p className="text-sm text-gray-600" data-id="element-1208">Order ID:</p>
                  <p className="font-medium" data-id="element-1209">{order.id}</p>
                </div>
                <div data-id="element-1210">
                  <p className="text-sm text-gray-600" data-id="element-1211">Date:</p>
                  <p className="font-medium" data-id="element-1212">
                    {new Date(order.date).toLocaleString()}
                  </p>
                </div>
                <div data-id="element-1213">
                  <p className="text-sm text-gray-600" data-id="element-1214">Status:</p>
                  <p className="font-medium" data-id="element-1215">{order.status}</p>
                </div>
                <div data-id="element-1216">
                  <p className="text-sm text-gray-600" data-id="element-1217">Total:</p>
                  <p className="font-medium" data-id="element-1218">₱{order.total.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} data-id="element-1219">
            <div className="mb-4" data-id="element-1220">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1221">
                Describe your issue*
              </label>
              <textarea id="description" name="description" rows={5} value={formData.description} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.description ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500`} placeholder="Please provide details about your issue..." data-id="element-1222" />
              {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-1223">
                  {errors.description}
                </p>}
            </div>
            <div className="mb-6" data-id="element-1224">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1225">
                Upload a photo (optional)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-1226">
                <div className="space-y-1 text-center" data-id="element-1227">
                  {formData.imageUrl ? <div data-id="element-1228">
                      <img src={formData.imageUrl} alt="Uploaded" className="mx-auto h-32 w-auto" data-id="element-1229" />
                      <button type="button" onClick={() => setFormData({
                    ...formData,
                    imageUrl: ''
                  })} className="mt-2 text-sm text-red-600 hover:text-red-800" data-id="element-1230">
                        Remove
                      </button>
                    </div> : <div data-id="element-1231">
                      <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-1232" />
                      <div className="flex text-sm text-gray-600" data-id="element-1233">
                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-amber-800 hover:text-amber-900 focus-within:outline-none" data-id="element-1234">
                          <span data-id="element-1235">Upload a file</span>
                          <input id="file-upload" name="file-upload" type="file" accept="image/*" className="sr-only" onChange={handleImageUpload} data-id="element-1236" />
                        </label>
                        <p className="pl-1" data-id="element-1237">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-1238">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </div>}
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-1239">
              <button type="button" onClick={() => navigate(-1)} className="mr-4 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1240">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isSubmitting ? 'bg-amber-600 cursor-not-allowed' : 'bg-amber-800 hover:bg-amber-900'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500`} data-id="element-1241">
                {isSubmitting ? 'Submitting...' : 'Submit Complaint'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
};
export default FileComplaint;